import * as Sentry from "@sentry/react";
import type {
  Connection,
  PublicKey,
  Signer,
  TransactionInstruction,
  VersionedTransactionResponse,
} from "@solana/web3.js";
import { Transaction } from "@solana/web3.js";

import { ReactComponent as PendingIcon } from "../../assets/notifications/pendingIcon.svg";
import { confirmOptions } from "../../constants/transaction";
import type { ConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { notify } from "../notifications";
import { getUnixTs, promiseDelay } from "../utils";
import { sendPostSendTxnErrorContent, StepTxnTimeoutError } from ".";
import type { NotificationContent, NotificationContentStates } from "./notifications";
import { sendSuccessContent } from "./notifications";

export const DEFAULT_IN_PROGRESS_NOTIFICATION_CONTENT: NotificationContent = {
  message: "Your transaction is pending",
  description: "Confirmation is in progress",
  icon: <PendingIcon />,
};

export const DEFAULT_SUCCESS_NOTIFICATION_CONTENT: NotificationContent = {
  message: "Transaction has been confirmed",
  description: "",
};

export const DEFAULT_APPROVE_TXN_MESSAGE = "Approve transactions from your wallet";

export const DEFAULT_NOTIFICATION_STATES: NotificationContentStates = {
  inProgress: DEFAULT_IN_PROGRESS_NOTIFICATION_CONTENT,
  success: DEFAULT_SUCCESS_NOTIFICATION_CONTENT,
};

export const sendPartialSignedTransaction = async (
  connection: Connection,
  wallet: ConnectedWallet,
  transaction: Transaction,
  awaitConfirmation = true,
  options = confirmOptions,
  renderNotifications = false,
  notificationContentOptions = DEFAULT_NOTIFICATION_STATES,
  onTransactionInProgress?: () => void,
  timeout = 45_000,
  pollInterval = 500,
  sendInterval = 10_000,
  sendRetries = 2,
) => {
  const notificationContent = {
    approveMessage: notificationContentOptions.approveMessage ?? DEFAULT_APPROVE_TXN_MESSAGE,
    inProgress: notificationContentOptions.inProgress ?? DEFAULT_IN_PROGRESS_NOTIFICATION_CONTENT,
    success: notificationContentOptions.success ?? DEFAULT_SUCCESS_NOTIFICATION_CONTENT,
  };

  if (typeof notificationContent.inProgress === "object" && !notificationContent.inProgress.icon) {
    notificationContent.inProgress.icon = <PendingIcon />;
  }

  notify({
    type: "open",
    message: notificationContent.approveMessage,
  });

  transaction = (await wallet.signTransaction(transaction)) as unknown as Transaction;
  const rawTransaction = transaction.serialize();
  const txid = await connection.sendRawTransaction(rawTransaction, options);

  sendSuccessContent(renderNotifications, txid, notificationContent.inProgress);

  if (onTransactionInProgress) {
    onTransactionInProgress();
  }

  if (awaitConfirmation) {
    const start = getUnixTs();
    let lastSendTimestamp = getUnixTs();
    let retries = 0;
    let response: VersionedTransactionResponse | null = null;

    while (getUnixTs() - start < timeout / 1000) {
      const timestamp = getUnixTs();
      if (retries < sendRetries && timestamp - lastSendTimestamp > sendInterval) {
        lastSendTimestamp = timestamp;
        retries += 1;
        await connection.sendRawTransaction(rawTransaction, options);
      }

      response = await connection.getTransaction(txid, {
        commitment: "confirmed",
        maxSupportedTransactionVersion: 2,
      });

      if (response) {
        if (!response.meta?.err) {
          sendSuccessContent(renderNotifications, txid, notificationContent.success);
        } else {
          sendPostSendTxnErrorContent(txid, response);
        }
        return txid;
      }
      await promiseDelay(pollInterval);
    }

    if (!response) {
      throw new StepTxnTimeoutError(`Transaction was not confirmed in ${(timeout / 1000).toFixed(0)} seconds.`, txid);
    }
  }

  return txid;
};

export const sendTransaction = async (
  connection: Connection,
  wallet: ConnectedWallet,
  instructions: TransactionInstruction[],
  signers: Signer[],
  awaitConfirmation = true,
  options = confirmOptions,
  renderNotifications = false,
  notificationContentOptions = DEFAULT_NOTIFICATION_STATES,
  onTransactionInProgress?: () => void,
  timeout = 45_000,
  pollInterval = 500,
  sendInterval = 10_000,
  sendRetries = 2,
  additionalAccounts: PublicKey[] = [],
) => {
  const notificationContent = {
    approveMessage: notificationContentOptions.approveMessage ?? DEFAULT_APPROVE_TXN_MESSAGE,
    inProgress: notificationContentOptions.inProgress ?? DEFAULT_IN_PROGRESS_NOTIFICATION_CONTENT,
    success: notificationContentOptions.success ?? DEFAULT_SUCCESS_NOTIFICATION_CONTENT,
  };

  if (typeof notificationContent.inProgress === "object" && !notificationContent.inProgress.icon) {
    notificationContent.inProgress.icon = <PendingIcon />;
  }

  let transaction = new Transaction();
  instructions.forEach((instruction) => transaction.add(instruction));
  transaction.recentBlockhash = (await connection.getLatestBlockhash("confirmed")).blockhash;
  // fee paid by the wallet owner
  transaction.feePayer = wallet.publicKey;
  //if additional accounts should be added to the tx, add them here in a wonky way
  if (additionalAccounts?.length > 0) {
    const txSizeBefore = transaction.serialize({ requireAllSignatures: false, verifySignatures: false }).length;
    if (txSizeBefore > 1232 - 32 * additionalAccounts.length) {
      const msg = `Cannot add additional accounts. tx is ${txSizeBefore} bytes long and trying to add ${additionalAccounts.length} pubkeys.`;
      console.error(msg);
      Sentry.captureMessage(msg);
    } else {
      const msg = transaction.compileMessage();
      additionalAccounts.forEach((account) => {
        msg.staticAccountKeys.push(account);
        msg.header.numReadonlyUnsignedAccounts++;
      });
      transaction = Transaction.populate(msg);
    }
  }
  if (signers.length > 0) {
    transaction.partialSign(...signers);
  }

  notify({
    type: "open",
    message: notificationContent.approveMessage,
  });

  transaction = (await wallet.signTransaction(transaction)) as unknown as Transaction;
  const rawTransaction = transaction.serialize();
  const txid = await connection.sendRawTransaction(rawTransaction, { ...options, skipPreflight: false });

  sendSuccessContent(renderNotifications, txid, notificationContent.inProgress);

  if (onTransactionInProgress) {
    onTransactionInProgress();
  }

  if (awaitConfirmation) {
    const start = getUnixTs();
    let lastSendTimestamp = getUnixTs();
    let retries = 0;
    let response: VersionedTransactionResponse | null = null;

    while (getUnixTs() - start < timeout / 1000) {
      const timestamp = getUnixTs();
      if (retries < sendRetries && timestamp - lastSendTimestamp > sendInterval) {
        lastSendTimestamp = timestamp;
        retries += 1;
        await connection.sendRawTransaction(rawTransaction, options);
      }

      response = await connection.getTransaction(txid, {
        commitment: "confirmed",
        maxSupportedTransactionVersion: 2,
      });

      if (response) {
        if (!response.meta?.err) {
          sendSuccessContent(renderNotifications, txid, notificationContent.success);
        } else {
          sendPostSendTxnErrorContent(txid, response);
        }
        return txid;
      }
      await promiseDelay(pollInterval);
    }

    if (!response) {
      throw new StepTxnTimeoutError(`Transaction was not confirmed in ${(timeout / 1000).toFixed(0)} seconds.`, txid);
    }
  }

  return txid;
};
